import * as tools from '../utils/tools';
import { Value } from './valueApi';
import EventEmitter from '../support/eventemitter';

/**
 * The API object to represent MVC GridRecord objects.
 * @class
 * @classdesc API Record Object
 */
export class Record extends EventEmitter {
    /**
     * Wrap the specified MVC GridRecord object within the specified API View object.
     * @param {View} parent - The API View object that contains this API Record object.
     * @param {GridRecord} _gridRecord - The source MVC GridRecord object this API Record object wraps.
     */
    constructor(parent, _gridRecord) {
        super();

        let self = this;

        /**
         * Internal reference to the MVC GridRecord object.
         * @type {GridRecord}
         */
        this.raw = _gridRecord;

        /**
         * Object that should be used for custom logic to prevent breaking the core API
         * @type {Object}
         */
        this.runtime = {};

        /**
         * Create a (circular) reference to this API Record object on the MVC GridRecord object.
         * @type {Record}
         */
        _gridRecord.apiRef = this;

        /**
         * The parent of a record is a view...EK
         * @type {View}
         */
        this.parent = parent;

        /**
         * Internal dictionary of API Value objects to sync with MVC GridValue objects.
         * @type {Dictionary}
         * @private
         */
        this._values = new tools.Dictionary();

        //Sync the current record list into the API reference list...EK
        this.raw.gridValues.Values.forEach((gv) => {
            self._values.add(gv.dynamicValue.PropertyID, new Value(self, gv), gv.property.UID);
        });

        //#region Dictionary listeners for syncing API Values with MVC GridValues

        //Add a listener for added/removed/cleared/etc. records to keep our API dictionary in sync...EK
        this.raw.gridValues.on('add', (addedValue) => {
            // add a new API Value object to wrap the GridValue object that was added to the source dictionary.
            self._values.add(addedValue.key, new Value(self, addedValue.value), addedValue.property.UID);
        });
        this.raw.gridValues.on('insert', (insertedItem, uid, atIndex) => {
            // insert a new API Value object to wrap the GridValue object that was inserted into the source dictionary.
            self._values.insert(insertedItem.key, new Value(self, insertedItem.value), uid, atIndex);
        });
        this.raw.gridValues.on('remove', (removedValue) => {
            // remove the API Value object so that we remain synced with the source dictionary.
            self._values.remove(removedValue.key);
        });
        this.raw.gridValues.on('swap', (firstItem, firstIndex, secondItem, secondIndex) => {
            // swap the API Value objects at the specified indices so that the dictionaries remain synced.
            self._values.swap(firstIndex, secondIndex);
        });
        this.raw.gridValues.on('append', (appendDictionary) => {
            // create a temp dictionary so we use it to create API Value objects for each GridValue object.
            let tmp = new tools.Dictionary();

            // make reference copies of the keys and uid arrays.
            tmp.Keys = appendDictionary.Keys.slice();
            tmp.KeysUID = appendDictionary.KeysUID.slice();

            // for each GridValue in the appended dictionary, create a new API Value in our temp dictionary.
            appendDictionary.Values.forEach((item) => {
                tmp.Values.push(new Value(self, item));
            });

            // append the new dictionary so we remain synced with the source dictionary.
            self._values.append(tmp);
        });
        this.raw.gridValues.on('updateKey', (originalKey, newKey) => {
            // we need to update the key values for our API object dictionary.
            self._values.updateKey(originalKey, newKey);
        });
        this.raw.gridValues.on('clear', () => {
            // the base dictionary was cleared; clear this one.
            self._values.clear();
        });

        //#endregion

        /**
         * Flag to indicate if this API object has been disposed or not.
         * @type {boolean}
         */
        this.isDisposed = false;
    }

    /**
     * @returns {Number} Returns the local database ID of this record.
     */
    get recordID() {
        return tools.getOrDefault(this, 'raw.clientRecord.Record.RecordID', null);
    }

    /**
     * Used to determine if a save is required for the current record (i.e. data has changed).
     * @returns {boolean|*}
     */
    get saveIsRequired() {
        return this.raw.isDirty();
    }

    /**
     * Exposes a dictionary of values for the parent record (may not match the display order).
     * @returns {Dictionary}
     */
    get values() {
        //Else: We already have the instance created, just return it...EK
        return this._values;
    }

    async dispose () {
        if (this.isDisposed) {
            return;
        }
        let disposedValues = [];
        this._values.Values.forEach((value) => {
            disposedValues.push(value.dispose());
        });
        await Promise.all(disposedValues);
        this._values.clear();
        this._values = null;
        this.raw.apiRef = null;
        this.raw = null;
        this.parent = null;

        this.isDisposed = true;
    }
}
